import React, { useMemo } from 'react';
import { Drawer } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

import { AppSidebarItemWithList } from 'features/app/components/AppSidebarItemWithList';
import { AppSidebarItem } from 'features/app/components/AppSidebarItem';
import { myGiftCardsPaths } from 'features/my-gift-cards/routes/paths';
import { subscriptionPaths } from 'features/subscription/routes/paths';
import { orderHistoryPaths } from 'features/order-history/routes/paths';
import { redemptionsPaths } from 'features/redemptions/routes/paths';
import { tutorialsPaths } from 'features/tutorials/routes/paths';
import { dashboardPaths } from 'features/dashboard/routes/paths';
import { financePaths } from 'features/finance/routes/paths';
import { usersPaths } from 'features/users/routes/paths';

import { Logo } from 'shared/components/Logo';
import { t } from 'locales/i18n';

import { ReactComponent as HomeIcon } from 'assets/icons/home-icon.svg';
import { ReactComponent as DiamondIcon } from 'assets/icons/diamond.svg';
import { ReactComponent as MyGiftCardsIcon } from 'assets/icons/gift-cards-icon.svg';
import { ReactComponent as OrderHistoryIcon } from 'assets/icons/order-history-icon.svg';
import { ReactComponent as UsersIcon } from 'assets/icons/users-icon.svg';
import { ReactComponent as RedemptionsIcon } from 'assets/icons/redemptions-icon.svg';
import { ReactComponent as TutorialsIcon } from 'assets/icons/tutorials-icon-icon.svg';
import { ReactComponent as FinanceIcon } from 'assets/icons/finance-icon.svg';

import { useStyles } from './styles';
import { hasRequiredRoles } from 'features/user/utils/hasRequiredRoles';
import useUser from 'features/user/hooks/useUser';
import { UserRole } from 'features/user/ts/UserRole';
import useAuth from 'features/auth/hooks/useAuth';
import { useSubscription } from 'features/user/hooks/useSubscription';

export const SIDEBAR_WIDTH = 345;

interface AppSidebarProps {
  show: boolean;
}

const SidebarItems = [
  {
    icon: HomeIcon,
    title: t('dashboard', { ns: 'app' }),
    href: dashboardPaths.static.root,
    bold: true,
    hasNestedRoutes: true,
    isForActiveSubscribe: false,
  },
  {
    icon: MyGiftCardsIcon,
    title: t('myRewards', { ns: 'app' }),
    href: myGiftCardsPaths.static.root,
    bold: false,
    hasNestedRoutes: true,
    isForActiveSubscribe: false,
  },
  {
    icon: OrderHistoryIcon,
    title: t('orderHistory', { ns: 'app' }),
    href: orderHistoryPaths.static.root,
    bold: false,
    isForActiveSubscribe: false,
  },
  {
    icon: RedemptionsIcon,
    title: t('redemptions', { ns: 'app' }),
    href: redemptionsPaths.static.root,
    bold: false,
    isForActiveSubscribe: false,
  },
  {
    icon: UsersIcon,
    title: t('users', { ns: 'app' }),
    href: usersPaths.static.root,
    bold: false,
    isForActiveSubscribe: false,
  },
  {
    icon: FinanceIcon,
    title: t('finance', { ns: 'app' }),
    requiredRoles: [
      {
        name: 'super_admin' as UserRole['name'],
      },
    ],
    listItems: [
      {
        title: t('balanceAndTopUp', { ns: 'app' }),
        href: financePaths.static.balanceAndTopUp,
      },
    ],
    hidden: false,
    isForActiveSubscribe: true,
  },
  {
    icon: TutorialsIcon,
    title: t('tutorials', { ns: 'app' }),
    href: tutorialsPaths.static.root,
    bold: false,
    isForActiveSubscribe: false,
  },
  {
    icon: DiamondIcon,
    title: t('subscription', { ns: 'app' }),
    requiredRoles: [
      {
        name: 'super_admin' as UserRole['name'],
      },
    ],
    href: subscriptionPaths.static.root,
    bold: true,
    hasNestedRoutes: true,
    isForActiveSubscribe: false,
    isForVerifiedUser: true,
  },
];

export const AppSidebar: React.FC<AppSidebarProps> = ({ show }) => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useUser();
  const { onHideUploadVerificationDocumentsModal } = useAuth();
  const { isSubscriptionActive } = useSubscription();

  const filteredSidebarItems = useMemo(() => {
    const filteredByRegistrationStatus = SidebarItems.filter(
      item =>
        !(!(user?.registration_request_status === 'APPROVED' && isSubscriptionActive) && item.isForActiveSubscribe)
    );
    const filteredByVerifiedUsers = filteredByRegistrationStatus.filter(
      item => !item?.isForVerifiedUser || (item?.isForVerifiedUser && user?.registration_request_status === 'APPROVED')
    );
    return filteredByVerifiedUsers.filter(item =>
      item.requiredRoles ? hasRequiredRoles(user?.roles ?? [], item.requiredRoles) : true
    );
  }, [user, isSubscriptionActive]);

  const handleSidebarItemClick = (href: string) => {
    onHideUploadVerificationDocumentsModal();
    history.push(href);
  };

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor={'left'}
      open={show}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <div className={classes.logoContainer}>
        <Logo />
      </div>
      <div className={classes.listContainer}>
        {filteredSidebarItems.map(
          (item, i) =>
            !item?.hidden &&
            (item.listItems ? (
              <AppSidebarItemWithList {...item} key={i} />
            ) : (
              <AppSidebarItem {...item} onSidebarItemClick={() => handleSidebarItemClick(item.href)} key={i} />
            ))
        )}
      </div>
    </Drawer>
  );
};
